import { Component, contentChild, effect, EventEmitter, Input, OnInit, Output, viewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { SearchInput, SearchWithCallback } from '@core/model/search.model';
import { SearchEvent } from '@core/model/pagination.model';
import { Subscription } from 'rxjs';
import { SearchInputComponent } from '../search-input/search-input.component';

@Component({
  selector: 'app-table-pagination-wrapper',
  templateUrl: './table-pagination-wrapper.component.html',
  styleUrl: './table-pagination-wrapper.component.scss',
  standalone: false,
})
export class TablePaginationWrapperComponent implements OnInit {
  private customSearchBox = contentChild<SearchInput>('searchBox');
  private defaultSearchBox = viewChild(SearchInputComponent);
  @Input() maintainedPageIndex: number;
  @Input() totalCount: number;
  @Input() count: number;
  @Input() sortByParamMap: Record<string, string>;
  @Input() sortByValue: string;
  @Input() sortOptions: Record<string, string>;
  @Input() searchTerm: string = '';
  @Input() label = 'Search';
  @Input() accessibleLabel: string;
  @Input() setQueryInParams: boolean = false;
  @Output() fetchData = new EventEmitter<SearchEvent>();
  public itemsPerPage = 15;
  public currentPageIndex = 0;
  private fragment: string;
  private tab: string;
  private subscriptions: Subscription = new Subscription();
  public form: FormGroup;

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
  ) {
    effect(() => {
      if (this.customSearchBox()) {
        this.setUpCustomSearchBox();
      }
    });
  }

  ngOnInit(): void {
    this.setupForm();
    if (this.maintainedPageIndex && this.maintainedPageIndex !== this.currentPageIndex) {
      this.currentPageIndex = this.maintainedPageIndex;
    }
  }

  get sortBySelected(): string {
    return this.form.get('sortBySelected')!.value;
  }

  set sortBySelected(newValue: string) {
    this.form.patchValue({ sortBySelected: newValue });
  }

  private setupForm(): void {
    const sortBySelected = this.sortByParamReverseLookup(this.sortByValue);
    this.form = this.formBuilder.group({ sortBySelected });

    this.form.get('sortBySelected')!.valueChanges.subscribe((newValue) => {
      this.sortBy(newValue);
    });
  }

  private sortByParamReverseLookup(sortByValue: string) {
    const sortByKeys = Object.keys(this.sortByParamMap);
    const defaultValue = sortByKeys.at(0);
    const sortBySelected = sortByKeys.find((key) => this.sortByParamMap[key] === sortByValue) ?? defaultValue;
    return sortBySelected;
  }

  private checkForFragment(): void {
    if (this.router.url.includes('#')) {
      this.fragment = this.router.url.split('#')[1];
      this.tab = this.fragment.split('-')[0];
    }
  }

  private addQueryParams(): void {
    this.checkForFragment();
    if (this.searchTerm) {
      this.router.navigate([], {
        fragment: this.fragment,
        queryParams: { search: this.searchTerm, tab: this.tab },
        queryParamsHandling: 'merge',
      });
    } else {
      this.router.navigate([], { fragment: this.fragment });
    }
  }

  public sortBy(sortType: string): void {
    this.sortByValue = this.sortByParamMap[sortType];
    this.currentPageIndex = 0;
    this.getData();
  }

  private setUpCustomSearchBox(): void {
    const customSearchBox = this.customSearchBox();
    if (!customSearchBox) {
      return;
    }

    this.subscriptions.add(
      customSearchBox.emitSearchEvent.subscribe(({ searchTerm, callback }) => {
        this.handleSearchWithCallback(searchTerm, callback);
      }),
    );
  }

  public handleSearch(searchTerm: string): void {
    this.currentPageIndex = 0;
    this.searchTerm = searchTerm;

    if (this.setQueryInParams) {
      this.addQueryParams();
    }
    this.getData();
  }

  public handleSearchWithCallback(searchTerm: string, callback: Function): void {
    if (!callback) {
      this.handleSearch(searchTerm);
      return;
    }

    const patchedCallback = (isSuccessful: boolean) => {
      callback(isSuccessful);
      if (isSuccessful) {
        this.searchTerm = searchTerm;
        this.currentPageIndex = 0;
      }
    };

    const searchEvent = {
      ...this.currentSearchParams,
      index: 0,
      callback: patchedCallback,
      searchTerm,
    };

    this.fetchDataWithParams(searchEvent);
  }

  public handlePageUpdate(pageIndex: number): void {
    this.currentPageIndex = pageIndex;
    this.getData();
  }

  public get currentSearchParams(): SearchEvent {
    return {
      index: this.currentPageIndex,
      itemsPerPage: this.itemsPerPage,
      searchTerm: this.searchTerm,
      sortByValue: this.sortByValue,
    };
  }

  public setStateWithoutEmitSearchEvent(params: SearchEvent) {
    const { sortByValue, index, searchTerm } = params;

    this.sortByValue = sortByValue;
    const sortBySelected = this.sortByParamReverseLookup(sortByValue);
    this.form.patchValue({ sortBySelected }, { emitEvent: false });

    this.currentPageIndex = index;

    const defaultSearchBox = this.defaultSearchBox();
    if (defaultSearchBox) {
      defaultSearchBox.setState({ searchTerm });
    }
  }

  private getData(): void {
    this.fetchDataWithParams(this.currentSearchParams);
  }

  private fetchDataWithParams(searchEvent: SearchEvent): void {
    this.fetchData.emit(searchEvent);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
